import { el, mount, unmount, setChildren, setAttr } from 'redom';

import { registerForOutsideClick } from 'generics/hooks/on-click-outside';

import { displayElementAroundAnchor } from 'utilities/element-position';
import {
  findIndexByPropertyValue
} from 'utilities/collection';

import {
  buildNativeOptions,
  buildSelectedOptionsLabel,
  getSelectedOptionValues
} from '../utilities';
import { defaultOnClose, getAllOptionIndices } from './utilities';

import singleSelectTypes from './types';

import SingleSelectOptions from './options';
import OptionsPanel from './options-panel';

import '../index.scss';

class DefaultButtonView {

  constructor() {

    this.el = el('label.single-select-label.ellipsis');

  }

  showPlaceholder(placeholderClassName) {

    if (this.el.classList.contains(placeholderClassName) === false) {
      this.el.classList.add(placeholderClassName);
    }

  }

  hidePlaceholder(placeholderClassName) {

    if (this.el.classList.contains(placeholderClassName) === true) {
      this.el.classList.remove(placeholderClassName);
    }

  }

  update(text) {

    this.el.textContent = text;

  }

}

class SingleSelectNative {

  constructor(options) {

    this.configureOptions(options);

    this.opened = false;

    this.cancelOutsideClickEventRegistration = null;
    this.LabelView = options.LabelView || null;
    this.ButtonView = options.ButtonView || DefaultButtonView;
    this.size = options.size || '';
    this.arrowIndicator = null;
    this.selectButton = null;
    this.singleSelectOptions = new SingleSelectOptions(this.singleSelectOptionWrapperClass, this.singleSelectOptionClass, options.LabelView, this.handleOptionClick.bind(this));
    this.optionsPanel = new OptionsPanel(this, options.LabelView);
    this.selectButtonContainer = null;
    this.selectOptionsContainer = null;

    this.initializeButton();
    this.initializeLayout();

    this.el = el('.single-select-wrapper', {
      id: this.id,
      class: 'native-single-select-wrapper ' + this.class + ' ' + this.size
    }, [this.selectButtonContainer, this.selectOptionsContainer]);
    this.value = getSelectedOptionValues(this.options, this.selectedOptionIndices);

    this.onInitialized(this.value);

  }

  configureOptions(options) {
    this.multiSelect = options.multiSelect === true;
    this.unselectable = this.multiSelect || options.unselectable === true;
    this.onChange = options.onChange || function noop() {};
    this.onSelect = options.onSelect || function noop() {};
    this.onUnselect = options.onUnselect || function noop() {};
    this.onClose = options.onClose || defaultOnClose;
    this.placeholder = options.placeholder;
    this.type = options.type || singleSelectTypes.DEFAULT;
    this.iconNode = options.icon;
    this.singleSelectButtonClass = options.singleSelectButtonClass;
    this.singleSelectOptionWrapperClass = options.singleSelectOptionWrapperClass;
    this.singleSelectOptionClass = options.singleSelectOptionClass;
    this.labelFieldName = options.labelFieldName || 'label';
    this.valueFieldName = options.valueFieldName || 'value';
    this.disabled = options.disabled;
    this.options = buildNativeOptions(options.options, this.labelFieldName, this.valueFieldName);
    this.selectedOptionIndices = options.selectedOptionIndices;
    this.onInitialized = options.onInitialized || function noop() {};
    this.context = options.context || {};
    this.class = options.class || '';
    this.id = options.id || performance.now();

  }

  initializeButton() {

    this.arrowIndicator = el('span.wind-unwind-indicator.unwind');
    this.selectButtonLabel = new this.ButtonView();
    this.displaySelectedOptions();
    this.selectButton = el(`button.single-select-label-button.${this.singleSelectButtonClass || ''}`);

    if (this.type === singleSelectTypes.DEFAULT) {
      setChildren(this.selectButton, [this.selectButtonLabel, this.arrowIndicator]);
    } else {
      setChildren(this.selectButton, [this.iconNode.cloneNode(true)]);
      this.selectButton.classList.add('button-mode');
    }

    if (this.disabled === true) {
      this.selectButton.classList.add('disabled');
    }

    this.selectButton.addEventListener('click', this.handleSelectButtonClick.bind(this));

  }

  initializeLayout() {

    this.selectButtonContainer = el('.single-select-button-container', this.selectButton);
    this.selectOptionsContainer = el('.single-select-options-container');

  }

  registerOutsideClickEventHandler() {

    const self = this;

    this.cancelOutsideClickEventRegistration = registerForOutsideClick({
      current: this.el
    }, function handleOutsideClick() {
      self.closeSelectOptions();
    });

  }

  unregisterOutsideClickEventHandler() {
    this.cancelOutsideClickEventRegistration();
  }

  handleSelectButtonClick(event) {

    event.stopPropagation();
    event.preventDefault();

    if (this.disabled === true) {
      return;
    }

    if (this.opened === true) {
      this.closeSelectOptions();
    } else {
      this.openSelectOptions();
    }

    return false;

  }

  addSelectedIndex(index) {

    const selectedOptionIndices = this.selectedOptionIndices;

    if (this.multiSelect === true) {
      selectedOptionIndices.push(index);
    } else {
      selectedOptionIndices[0] = index;
    }

  }

  removeSelectedIndex(indexAmongSelected) {

    const selectedOptionIndices = this.selectedOptionIndices;

    if (this.multiSelect === true) {
      selectedOptionIndices.splice(indexAmongSelected, 1);
    } else {
      selectedOptionIndices.pop();
    }

  }

  computeValueAndPropagateLatest({ actionType, options }) {

    this.displaySelectedOptions();
    this.updateOptionsPanel();

    this.value = getSelectedOptionValues(this.options, this.selectedOptionIndices);

    this.onChange({ ...this.value, actionType }, this.selectedOptionIndices);

    if (actionType === 'selected') {
      this.onSelect(options);
    } else {
      this.onUnselect(options);
    }

  }

  handleOptionClick(index, option) {

    const selectedOptionIndices = this.selectedOptionIndices;
    const indexAmongSelected = selectedOptionIndices.indexOf(index);

    let actionType = 'selected';

    if (this.unselectable === false) {

      if (indexAmongSelected !== -1) {
        return;
      }

      this.addSelectedIndex(index);

    } else {

      if (indexAmongSelected === -1) {
        this.addSelectedIndex(index);
      } else {
        this.removeSelectedIndex(indexAmongSelected);
        actionType = 'unselected';
      }

    }

    if (this.multiSelect === false) {
      this.closeSelectOptions();
    }

    this.computeValueAndPropagateLatest({ actionType, options: [option] });

  }

  displaySelectedOptions() {

    let labelEmpty = false;

    if (this.selectedOptionIndices.length === 1 && this.LabelView !== null) {

      this.selectButtonLabel.update('');
      const labelView = new this.LabelView();
      labelView.update(this.options[this.selectedOptionIndices[0]], this.context, 'button');
      setChildren(this.selectButtonLabel.el, [labelView]);

    } else {

      const label = buildSelectedOptionsLabel(this.options, this.selectedOptionIndices);
      const labelText = label || this.placeholder;

      labelEmpty = label === '';
      this.selectButtonLabel.update(labelText);

    }

    const placeholderClassName = 'placeholder';

    if (labelEmpty === true) {
      this.selectButtonLabel.showPlaceholder(placeholderClassName);
    } else {
      this.selectButtonLabel.hidePlaceholder(placeholderClassName);
    }

    if (this.disabled === true) {
      this.selectButton?.classList.add('disabled');
    } else {
      this.selectButton?.classList.remove('disabled');
    }

  }

  closeSelectOptions() {

    this.opened = false;

    this.arrowIndicator.classList.toggle('unwind');
    this.arrowIndicator.classList.toggle('wind');

    this.onClose(getSelectedOptionValues(this.options, this.selectedOptionIndices), this.selectedOptionIndices);

    unmount(this.selectOptionsContainer, this.optionsPanel);

    this.unregisterOutsideClickEventHandler();

  }

  updateOptionsPanel() {

    this.optionsPanel.update();

  }

  positionOptions() {

    const element = this.optionsPanel.el;
    const anchorElement = this.selectButtonContainer.querySelector('.single-select-label-button');
    const viewportElement = void 0;
    const verticalOffset = 5;
    const horizontalOffset = 0;
    const startSide = 'bottom';
    const placeOnCenter = true;
    const positionableSides = ['bottom', 'top'];

    setAttr(element, { style: { width: anchorElement.getBoundingClientRect().width + 'px', height: 'auto' } });

    displayElementAroundAnchor(element, anchorElement, viewportElement, verticalOffset, horizontalOffset, startSide, placeOnCenter, positionableSides);

  }

  openSelectOptions() {

    this.opened = true;

    // arrows up(or down is it?)
    this.arrowIndicator.classList.toggle('unwind');
    this.arrowIndicator.classList.toggle('wind');

    // generate elements for latest options
    this.optionsPanel.update(this.options, this);

    // mount them
    mount(this.selectOptionsContainer, this.optionsPanel);

    this.positionOptions();

    // const singleSelectOptionsContainer = this.optionsPanel.el.el;

    // // position them
    // positionSingleSelectOptions(singleSelectOptionsContainer, this.selectButton);

    // // position them again :-D
    // createPopper(this.selectButtonContainer, singleSelectOptionsContainer, {
    //   placement: 'bottom',
    //   modifiers: [{
    //     name: 'offset',
    //     options: {
    //       offset: [0, 5]
    //     }
    //   }]
    // });

    // // try & scroll to selected option
    // const activeSelectOption = singleSelectOptionsContainer.querySelector('.active-select-option');

    // if (activeSelectOption !== null) {
    //   activeSelectOption.scrollIntoView();
    // }

    // wait for outside click
    this.registerOutsideClickEventHandler();

  }

  isSelectedOption(index) {

    return this.selectedOptionIndices.indexOf(index) !== -1;

  }

  updateOptions(options, equalityFieldName) {

    const selectedOptionIndices = this.selectedOptionIndices;
    const _options = buildNativeOptions(options, this.labelFieldName, this.valueFieldName);
    const selectedOptions = [];

    for (let selectedOptionIndex of selectedOptionIndices) {
      selectedOptions.push(this.options[selectedOptionIndex]);
    }

    const _selectedOptionIndices = [];
    const optionsLength = options.length;

    for (let index = 0; index < optionsLength; index++) {

      const option = _options[index];

      for (let selectedOption of selectedOptions) {

        if (option[equalityFieldName] === selectedOption[equalityFieldName]) {
          _selectedOptionIndices.push(index);
          // No need to check this option next time. So removing from selectedOptions
          selectedOptions.splice(selectedOptions.indexOf(selectedOption), 1);
          break;
        }

      }

    }

    this.selectedOptionIndices = _selectedOptionIndices;
    this.options = _options;

  }

  configure(options) {

    this.configureOptions(options);
    this.displaySelectedOptions();
    this.updateOptionsPanel();

  }

  getValues() {

    if (this.value === null) {
      return [];
    }

    return this.value.values;

  }

  getValue() {

    if (this.value === null) {
      return null;
    }

    const values = this.value.values;

    if (values.length === 0) {
      return null;
    }

    return values[0];

  }

  setValues(values = []) {

    const selectedOptionIndices = [];
    const options = this.options;

    for (let value of values) {

      const valueIndex = findIndexByPropertyValue(options, 'value', value);

      if (valueIndex === -1) {
        continue;
      }

      selectedOptionIndices.push(valueIndex);

    }

    if (selectedOptionIndices.length === 0) {
      return;
    }

    this.selectedOptionIndices = selectedOptionIndices;

    this.displaySelectedOptions();
    this.updateOptionsPanel();
    this.value = getSelectedOptionValues(this.options, this.selectedOptionIndices);

  }

  setValue(value) {

    this.setValues([value]);

  }

  focus(soft = false) {

    this.selectButton.focus();

    if (soft === false) {
      this.openSelectOptions();
    }

  }

  setReadOnly(readOnly) {

    this.disabled = readOnly;

    if (this.disabled === true) {
      this.selectButton.classList.add('disabled');
    } else {
      this.selectButton.classList.remove('disabled');
    }

  }

  determineAllSelected() {

    return this.selectedOptionIndices.length === this.options.length;

  }

  selectAllOptions() {

    this.selectedOptionIndices = getAllOptionIndices(this.options);

    this.computeValueAndPropagateLatest({ actionType: 'selected', options: [...this.options] });

  }

  unselectAllOptions() {

    this.selectedOptionIndices = [];

    this.computeValueAndPropagateLatest({ actionType: 'unselected', options: [] });

  }

  reset() {

    this.selectedOptionIndices = [];

    this.displaySelectedOptions();
    this.updateOptionsPanel();

  }

}

export default SingleSelectNative;